import { useState } from "react";
import { ImageIcon, Images, RotateCcw, Trash2, Upload } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";

import type { HostImageSlot, SiteContent } from "../../../api/types";
import {
  groupSlotsByPage,
  resetSlotImage,
  setSlotImage,
  siteImageChoices,
  slotImage,
  type SlotImage,
} from "../../../model/hostExtras";
import { CropUploadDialog } from "./CropUploadDialog";
import { defaultSourceText, imageSlotLabel, NO_IMAGE_TEXT, pageCn } from "./hostLabels";
import { ProxiedImage } from "./ProxiedImage";

type Slot = HostImageSlot & { index: number };

/** 这一格的状态说明(含清空后的效果)。 */
function stateText(content: SiteContent, slot: Slot, img: SlotImage): string {
  switch (img.state) {
    case "override":
      return "已换成你的图";
    case "auto":
      return "自动配图(可换可清)";
    case "cleared":
      return `已清空 · 发布后这里显示「${NO_IMAGE_TEXT}」`;
    case "default": {
      const m = /^product:(\d+)$/.exec(slot.defaultSource ?? "");
      const name = m ? content.products[Number(m[1])]?.name : undefined;
      return (
        defaultSourceText(slot.defaultSource, typeof name === "string" ? name : undefined) ??
        "已设置"
      );
    }
    default:
      return slot.templateHasImage
        ? `未配图 · 模板原图发布时会清空,这里显示「${NO_IMAGE_TEXT}」`
        : "未配图";
  }
}

/**
 * 1009 编辑页「图片 · 各页面图片位」:模板里**全部**图片位按页分组,每格可上传(指定图片位上传,purpose=slot)/
 * 从本站已有图里选 / 清空 / 恢复默认(跟随 logo / 首图 / 产品图)。
 * ★ 写进本地草稿(置脏),保存草稿才落库;模板原图永远不会用(那是原企业的图)。
 */
export function HostSlotImagePanel({
  content,
  slots,
  pageOrder,
  upload,
  onChange,
  disabled,
}: {
  content: SiteContent;
  slots: readonly HostImageSlot[];
  pageOrder?: readonly string[] | null;
  /** 裸 base64 + 图片位 token → 完整 url。 */
  upload: (token: string, base64: string) => Promise<string>;
  onChange: (update: (prev: SiteContent) => SiteContent) => void;
  disabled?: boolean;
}) {
  const [active, setActive] = useState<Slot | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  const groups = groupSlotsByPage(slots, pageOrder);
  const choices = siteImageChoices(content);
  if (slots.length === 0) return null;

  return (
    <section
      aria-label="各页面图片位"
      className={cn("space-y-3 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <div>
        <h2 className="text-sm font-semibold text-foreground">图片 · 各页面图片位</h2>
        <p className="mt-0.5 text-2xs text-muted-foreground">
          模板里每个放图的位置都能换成你的图;没配图的位置发布后显示「{NO_IMAGE_TEXT}
          」,不会用模板原企业的图。
        </p>
      </div>
      {groups.map((g) => (
        <div
          key={g.page}
          role="group"
          aria-label={`${pageCn(g.page)} 图片位`}
          className="space-y-2"
        >
          <h3 className="text-xs font-medium text-muted-foreground">{pageCn(g.page)}</h3>
          <ul className="grid gap-2 sm:grid-cols-2">
            {g.slots.map((slot) => {
              const label = imageSlotLabel(slot);
              const img = slotImage(content, slot);
              const touched =
                img.state === "override" || img.state === "auto" || img.state === "cleared";
              return (
                <li
                  key={slot.token}
                  role="group"
                  aria-label={label}
                  data-token={slot.token}
                  data-state={img.state}
                  className="flex gap-3 rounded-md border border-border/70 p-2"
                >
                  <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded bg-muted">
                    {img.value ? (
                      <ProxiedImage
                        value={img.value}
                        alt={label}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="flex flex-col items-center gap-0.5 text-2xs text-muted-foreground">
                        <ImageIcon className="h-4 w-4" aria-hidden />
                        {NO_IMAGE_TEXT}
                      </span>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="truncate text-xs font-medium text-foreground">{label}</span>
                    <span className="text-2xs text-muted-foreground">
                      {stateText(content, slot, img)}
                    </span>
                    <div className="mt-auto flex flex-wrap gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        disabled={disabled}
                        aria-label={`上传:${label}`}
                        onClick={() => setActive(slot)}
                      >
                        <Upload className="h-3 w-3" />
                        上传
                      </Button>
                      <Popover
                        open={picking === slot.token}
                        onOpenChange={(o) => setPicking(o ? slot.token : null)}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            disabled={disabled || choices.length === 0}
                            aria-label={`选图:${label}`}
                            title={
                              choices.length === 0
                                ? "本站还没有图,先上传 Logo / 首图 / 产品图"
                                : undefined
                            }
                          >
                            <Images className="h-3 w-3" />
                            选图
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-2">
                          <ul aria-label="本站已有图" className="grid grid-cols-3 gap-2">
                            {choices.map((c) => (
                              <li key={c.url}>
                                <button
                                  type="button"
                                  className="flex w-full flex-col items-center gap-1 rounded border border-border/70 p-1 text-2xs hover:border-primary"
                                  aria-label={`用这张:${c.label}`}
                                  onClick={() => {
                                    onChange((prev) => setSlotImage(prev, slot.token, c.url));
                                    setPicking(null);
                                  }}
                                >
                                  <span className="grid h-12 w-full place-items-center overflow-hidden rounded bg-muted">
                                    <ProxiedImage
                                      value={c.url}
                                      alt={c.label}
                                      className="h-full w-full object-cover"
                                    />
                                  </span>
                                  <span className="w-full truncate">{c.label}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </PopoverContent>
                      </Popover>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        disabled={disabled || img.state === "cleared" || img.state === "none"}
                        aria-label={`清空:${label}`}
                        onClick={() => onChange((prev) => setSlotImage(prev, slot.token, ""))}
                      >
                        <Trash2 className="h-3 w-3" />
                        清空
                      </Button>
                      {touched && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          disabled={disabled}
                          aria-label={`恢复默认:${label}`}
                          title={
                            slot.defaultSource
                              ? (defaultSourceText(slot.defaultSource) ?? undefined)
                              : "不单独配图(发布后清空)"
                          }
                          onClick={() => onChange((prev) => resetSlotImage(prev, slot.token))}
                        >
                          <RotateCcw className="h-3 w-3" />
                          恢复默认
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      <CropUploadDialog
        open={active !== null}
        onOpenChange={(o) => {
          if (!o) setActive(null);
        }}
        title={active ? `上传 · ${imageSlotLabel(active)}` : "裁剪后上传"}
        defaultAspect={
          active?.role === "img-logo" ? 1 : active?.role === "img-hero" ? 16 / 9 : null
        }
        upload={(b64) =>
          active ? upload(active.token, b64) : Promise.reject(new Error("没有选中图片位"))
        }
        onDone={(url) => {
          if (active) {
            const token = active.token;
            onChange((prev) => setSlotImage(prev, token, url));
          }
        }}
      />
    </section>
  );
}
